import React from "react"
import { PoolDataType, UserShareType } from "src/hooks/usePoolData"
import { Box } from "@mui/material"
import CustomCard from "./CustomCard"
import { commify } from "ethers/lib/utils"
import {
  POOLS_MAP,
  POOL_FEE_PRECISION,
  PoolName,
  PoolTypes,
  TOKENS_MAP,
} from "src/constants"
import { formatBNToPercentString, formatBNToString } from "src/utils"
import Text from "./Text"
import { useTranslation } from "react-i18next"
import { BIG_ZERO } from "src/constants/bigNumber"
import { AddToMetamask } from "./AddToMetamask"
import QuestionHelper from "src/components/QuestionHelper"

interface Props {
  userData: UserShareType | undefined
  poolData: PoolDataType
  poolName: PoolName
}

const PoolInfoCard = ({ userData, poolData, poolName }: Props) => {
  const { t } = useTranslation()
  const pool = POOLS_MAP[poolName]
  const { type: poolType } = pool
  const formattedDecimals = poolType === PoolTypes.USD ? 2 : 4

  const formattedUserData = {
    share: userData ? formatBNToPercentString(userData.share, 18) : "-",
    totalAmount: userData
      ? commify(formatBNToString(userData.totalAmount, 18, formattedDecimals))
      : "-",
    tokens: userData
      ? userData.tokens.map((coin) => {
          const token = TOKENS_MAP[coin.symbol]
          return {
            symbol: token.symbol,
            name: token.name,
            value: commify(formatBNToString(coin.value, 18, formattedDecimals)),
          }
        })
      : [],
  }

  const formattedPoolData = {
    virtualPrice: poolData?.virtualPrice
      ? commify(formatBNToString(poolData.virtualPrice, 18, 5))
      : "-",
    reserve: poolData?.reserve
      ? commify(formatBNToString(poolData.reserve, 18, 2))
      : "-",
    volume: poolData?.volume
      ? commify(formatBNToString(poolData.volume, 0, 0))
      : "-",
    tokens:
      poolData?.tokens.map((coin) => {
        const token = TOKENS_MAP[coin.symbol]
        return {
          addresses: token.addresses,
          symbol: token.symbol,
          name: token.name,
          icon: token.icon,
          percent: coin.percent,
          value: commify(formatBNToString(coin.value, 18, formattedDecimals)),
          decimals: token.decimals,
        }
      }) || [],
    totalLocked: poolData?.totalLocked
      ? commify(formatBNToString(poolData.totalLocked, 18, 2))
      : "-",
    tokenBalancesSum: poolData?.tokens
      ? commify(
          formatBNToString(
            poolData.tokens.reduce((sum, b) => sum.add(b.value), BIG_ZERO),
            18,
            2,
          ),
        )
      : "-",
    swapFee: poolData?.swapFee
      ? formatBNToPercentString(poolData.swapFee, POOL_FEE_PRECISION)
      : "-",
    adminFee:
      poolData?.swapFee && poolData.adminFee
        ? `${formatBNToPercentString(
            poolData.adminFee,
            POOL_FEE_PRECISION,
          )} of ${formatBNToPercentString(
            poolData.swapFee,
            POOL_FEE_PRECISION,
          )}`
        : null,
  }

  return (
    <CustomCard variant="darkest-gray" maxWidth="564px" mx="auto" withGradient>
      <Box>
        <Text variant="h5" mb={2}>
          {t("My Liquidity")}
        </Text>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Text>{t("Share of Pool")}</Text>
          <Text>{formattedUserData.share}</Text>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Text>{t("Total Amount")}</Text>
          <Box display="flex" alignItems="center">
            <Text>{formattedUserData.totalAmount}</Text>
          </Box>
        </Box>
      </Box>
      <Text gradient="gradient2" height="1px" mx="auto" my={3} />
      <Box>
        <Text variant="h5" mb={2}>
          {t("Currency Reserves")}
        </Text>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Text>{t("Total LP tokens")}</Text>
          <Box display="flex" alignItems="center">
            <Text mr={1}>{formattedPoolData.totalLocked}</Text>
            <img src={pool.lpToken.icon} height={16} />
          </Box>
        </Box>
        {formattedPoolData.tokens.map((token) => (
          <Box
            display="flex"
            justifyContent="space-between"
            key={token.symbol}
            mt={1}
          >
            <Box display="flex" alignItems="center">
              <img alt="icon" src={token.icon} height={16} width={16} />
              <AddToMetamask token={token} />
              <Text ml={0.5}>{token.symbol}</Text>
            </Box>
            <Text>
              {token.value || "-"} {`(${token.percent})`}
            </Text>
          </Box>
        ))}
      </Box>
      <Text gradient="gradient2" height="1px" mx="auto" my={3} />
      <Box>
        <Text variant="h5" mb={2}>
          {t("Pool Info")}
        </Text>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Text>{t("Trading Fee")}</Text>
          <Box display="flex" alignItems="center">
            <Text>{formattedPoolData.swapFee}</Text>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center">
            <Text>{t("Virtual Price")}</Text>
            <QuestionHelper
              text={t(
                "The current price of the pool LP token relative to the underlying pool assets",
              )}
              ml="4px"
            />
          </Box>
          <Box display="flex" alignItems="center">
            <Text>{formattedPoolData.virtualPrice}</Text>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Text>{t("Total Liquidity")}</Text>
          <Box display="flex" alignItems="center">
            <Text>{formattedPoolData.reserve} USD</Text>
          </Box>
        </Box>
      </Box>
    </CustomCard>
  )
}

export default PoolInfoCard
